import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { MuiThemeProvider, createTheme, IconButton, InputAdornment, withStyles } from '@material-ui/core';

import Check from '@material-ui/icons/Check';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

// import DatePicker from "@material-ui/pickers/DatePicker";
// import DateFnsUtils from "@material-ui/pickers/utils/date-fns-utils";

import {
  format,
  isValid,
  isSameDay,
  startOfWeek,
  endOfWeek,
  subDays,
  addDays,
  addHours,
  isWithinInterval,
} from 'date-fns';
import frLocale from 'date-fns/locale/fr';

import { formattedDate } from 'helpers/helpers';

import { blackColor, primaryColor, successColor, dangerColor, lightGrayColor } from 'assets/styles/jss/main';
import styles from './style/weekPickerStyle';

class LocalizedUtils extends DateFnsUtils {
  getDatePickerHeaderText(date) {
    return format(Date.parse(date), 'd MMM yyyy', { locale: this.locale });
  }
  getCalendarHeaderText(date) {
    return format(Date.parse(date), 'MMM yyyy', { locale: this.locale });
  }
  getYearText(date) {
    return format(Date.parse(date), 'yyyy', { locale: this.locale });
  }
  getWeekdays() {
    return ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
  }
}

class WeekPicker extends PureComponent {
  handleWeekChange = (date) => {
    const modifiedStartDate = addHours(startOfWeek(date, { weekStartsOn: 1 }), 2);
    const modifiedEndDate = subDays(endOfWeek(date, { weekStartsOn: 1 }), 2);
    this.props.handleChange({
      startDate: modifiedStartDate,
      endDate: modifiedEndDate,
    });
  };

  formatWeekSelectLabel = (date, invalidLabel) => {
    if (date === null) return '';

    return isValid(date)
      ? `Semaine du ${format(startOfWeek(Date.parse(date), { weekStartsOn: 1 }), 'd MMM yyyy', {
          locale: frLocale,
        })}`
      : invalidLabel;
  };

  renderWrappedWeekDay = (date, selectedDate, dayInCurrentMonth) => {
    const { classes, disablePast } = this.props;

    const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
    const end = addDays(start, 4);

    const dayDisabled =
      !dayInCurrentMonth ||
      date < endOfWeek(disablePast ? Date.now() : new Date(new Date().getFullYear(), 0, 1), { weekStartsOn: 1 });
    const dayIsBetween = isWithinInterval(date, { start, end });
    const isFirstDay = isSameDay(date, start);
    const isLastDay = isSameDay(date, end);

    const wrapperClassName = cx({
      [classes.highlight]: dayIsBetween,
      [classes.firstHighlight]: isFirstDay,
      [classes.endHighlight]: isLastDay,
    });

    const dayClassName = cx(classes.day, {
      [classes.nonCurrentMonthDay]: dayDisabled,
      [classes.highlightNonCurrentMonthDay]: dayDisabled && dayIsBetween,
    });

    return (
      <div className={wrapperClassName}>
        <IconButton className={dayClassName}>
          <span> {format(Date.parse(date), 'd')} </span>
        </IconButton>
      </div>
    );
  };

  render() {
    const { classes, startDate, success, error, disablePast, label, hideLabel } = this.props;

    const successErrorBorderColor = success ? successColor : error ? dangerColor : '#ececee';

    const materialTheme = createTheme({
      overrides: {
        MuiFormControl: {
          root: {
            width: '100%',
            paddingBottom: 10,
          },
        },
        MuiFormLabel: {
          root: {
            color: blackColor,
            fontSize: 16,
            fontWeight: 400,
            fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
            lineHeight: 1.42857,
            transform: 'translate(0, 0) scale(1) !important',
            position: 'relative !important',
            '&$focused': {
              '&$focused': {
                color: blackColor,
              },
            },
          },
        },
        MuiInput: {
          input: {
            position: 'relative',
            padding: '2px 25px 2px 12px',
            fontWeight: '400',
            height: '36px',
            fontSize: '16px',
            lineHeight: '1.428571429',
            color: blackColor,
            textOverflow: 'ellipsis',
            border: `2px solid ${successErrorBorderColor}`,
            background: lightGrayColor,
            borderRadius: '5px',
            cursor: 'pointer',
          },
          underline: {
            '&:before': {},
            '&:hover:before': {},
            '&:after': {},
          },
        },
        MuiPickersToolbar: {
          toolbar: {
            backgroundColor: primaryColor,
          },
        },
        MuiPickersModal: {
          dialogAction: {
            color: primaryColor,
          },
        },
      },
    });

    const endAdornment = {
      endAdornment: (
        <InputAdornment position='end' className={classes.successCheckWrapper}>
          <Check className={classes.successCheck} />
        </InputAdornment>
      ),
    };

    return (
      <MuiPickersUtilsProvider utils={LocalizedUtils} locale={frLocale}>
        <MuiThemeProvider theme={materialTheme}>
          <DatePicker
            autoOk
            label={
              label ? (
                <span>{label}</span>
              ) : !hideLabel ? (
                <span>
                  Période de stage <small>(obligatoire)</small>
                </span>
              ) : (
                ''
              )
            }
            format='d MMM yyyy'
            cancelLabel='Annuler'
            disablePast={disablePast}
            minDateMessage='Choisir une date postérieure'
            {...(disablePast && {
              minDate: formattedDate(endOfWeek(Date.now(), { weekStartsOn: 2 })),
            })}
            value={startDate}
            onChange={this.handleWeekChange}
            renderDay={this.renderWrappedWeekDay}
            labelFunc={this.formatWeekSelectLabel}
            InputProps={{
              ...(success && endAdornment),
              disableUnderline: true,
            }}
          />
        </MuiThemeProvider>
      </MuiPickersUtilsProvider>
    );
  }
}

WeekPicker.propsTypes = {
  classes: PropTypes.object.isRequired,
};

WeekPicker.defaultProps = {
  disablePast: true,
};

export default withStyles(styles)(WeekPicker);
